import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import { memo } from 'react'

export interface UploadProgressV2Props {
  progress: number
  currentStep: string | null
  totalSteps: number
  completedSteps: number
  isUploading?: boolean
  isCancelling?: boolean
  onCancel?: () => void
}

export const UploadProgressV2 = memo((props: UploadProgressV2Props) => {
  const {
    progress,
    currentStep,
    totalSteps,
    completedSteps,
    isUploading = true,
    isCancelling = false,
    onCancel,
  } = props

  // Show loading spinner when uploading but no progress data yet
  const showInitialLoading = isUploading && !currentStep && progress === 0

  return (
    <Stack p="20px 0px" width="280px" alignItems="center">
      {showInitialLoading ? (
        // Initial uploading state - show spinner
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} />
          <Typography
            sx={{
              fontSize: '14px',
              color: '#000',
            }}
          >
            Uploading file...
          </Typography>
        </Stack>
      ) : (
        // Progress state - show progress bar
        <Stack width="100%">
          <Typography
            sx={{
              marginBottom: '8px',
              fontSize: '14px',
              lineHeight: '120%',
              textAlign: 'center',
            }}
          >
            {totalSteps > 0
              ? `Importing Data (${completedSteps} / ${totalSteps})`
              : 'Importing Data...'}
          </Typography>
          {currentStep && (
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '120%',
                textAlign: 'center',
                marginBottom: '16px',
              }}
            >
              {currentStep}...
            </Typography>
          )}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress
                  color="primary"
                  variant={progress > 0 ? 'determinate' : 'indeterminate'}
                  value={progress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Box sx={{ minWidth: 45 }}>
                <Typography variant="body2" color="text.main">
                  {`${Math.round(progress)}%`}
                </Typography>
              </Box>
            </Box>
          </Box>
          {onCancel && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="secondary"
                onClick={onCancel}
                disabled={isCancelling}
                sx={{ minWidth: 100 }}
              >
                {isCancelling ? 'Stopping...' : 'Cancel'}
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  )
})

UploadProgressV2.displayName = 'UploadProgressV2'

export default UploadProgressV2
